<template>
  <div class="ridership-by-route-chart">
    <div v-if="!hasData" class="text-center text-muted py-5">
      {{ $t('flexigo.ridership.noData') }}
    </div>
    <highcharts v-else ref="chart" :options="chartOptions" />
  </div>
</template>

<script>
import Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { Chart } from 'highcharts-vue';
import { format } from 'date-fns';
import { localFormatTimeString } from '@/helpers/dateTime';
import { escapeHtml } from '@/helpers/string';
import { getBrowserLocaleFormat } from '@/helpers/dateFormatter';

HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

/**
 * Tooltip rows are label + value on one line, and the box widens to fit them (VSER-14676).
 * `white-space: nowrap` is what buys that: Highcharts measures the useHTML label with
 * shrink-to-fit, so wrappable content let the box collapse towards its longest word and the
 * rows broke over several lines in a narrow column.
 */
const TOOLTIP_SCROLL_STYLE = 'pointer-events:auto;white-space:nowrap';

/**
 * The name cell of a row. It keeps its ceiling so that one very long route or stop name
 * cannot push the tooltip wider than the chart — past it the name ellipsizes rather than
 * wraps, and `title` keeps the full text reachable. `min-width:0` is needed for that: a flex
 * item's automatic minimum is its content width, and a minimum always beats a maximum.
 */
const tooltipNameCell = (name, color = '#cbd5e0') => {
  // Route and stop names are entered by admins, and land here in an attribute and in element
  // content — both have to be escaped, or a quote alone breaks out of `title`.
  const safeName = escapeHtml(name);
  return `<span title="${safeName}" style="color:${color};min-width:0;max-width:360px;overflow:hidden;text-overflow:ellipsis">${safeName}</span>`;
};

export default {
  name: 'RidershipByRouteChart',
  components: {
    highcharts: Chart,
  },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    /**
     * Long-form category labels, used by the tooltip header where there's room
     * for the full date. The x-axis itself uses the compact `categories` so
     * dense ranges stay legible. Empty for views whose axis labels are already
     * complete (time of day, weekday) — the tooltip then falls back to `this.x`.
     */
    fullCategories: {
      type: Array,
      default: () => [],
    },
    series: {
      type: Array,
      default: () => [],
    },
    occupancy: {
      type: Array,
      default: () => [],
    },
    viewMode: {
      type: String,
      default: 'byRun',
    },
    dateRange: {
      type: Object,
      default: null,
    },
    direction: {
      type: String,
      default: 'ALL',
    },
    referenceTime: {
      type: String,
      default: 'firstStopDeparture',
    },
    fromTime: {
      type: String,
      default: null,
    },
    toTime: {
      type: String,
      default: null,
    },
    isSingleRoute: {
      type: Boolean,
      default: false,
    },
    routeName: {
      type: String,
      default: '',
    },
    interval: {
      type: String,
      default: '30min',
    },
  },
  computed: {
    hasData() {
      return this.categories.length > 0 && this.series.length > 0;
    },
    intervalSuffix() {
      const map = {
        '15min': this.$t('flexigo.ridership.intervalSuffix15Min'),
        '30min': this.$t('flexigo.ridership.intervalSuffix30Min'),
        '1hour': this.$t('flexigo.ridership.intervalSuffix1Hour'),
      };
      return map[this.interval] || map['1hour'];
    },
    /**
     * Bucket size in minutes for the active interval toggle.
     * Used by the multi-route tooltip to compute each bar's own time range.
     */
    intervalMinutes() {
      const map = { '15min': 15, '30min': 30, '1hour': 60 };
      return map[this.interval] || 60;
    },
    /**
     * Detects gaps between consecutive time-of-day categories and produces
     * Highcharts plotLines with "+Nh" labels at each gap midpoint.
     *
     * Only applies to the "by Run" (time-of-day) view where categories are
     * "HH:mm" strings — the issue is that a 4-hour gap between 10:00 and 14:00
     * renders as adjacent bars with no visual indication of the jump. Highcharts
     * has no built-in gap-label feature, so we inject dashed plotLines with a
     * "+3h" style label between the two categories.
     *
     * Returns an array of plotLine configs ready for `xAxis.plotLines`.
     */
    gapPlotLines() {
      if (this.viewMode !== 'byRun') return [];
      const { categories, intervalMinutes } = this;
      if (!categories || categories.length < 2) return [];
      const toMins = (label) => {
        const m = String(label).match(/^(\d{1,2}):(\d{2})$/);
        return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
      };
      const lines = [];
      for (let i = 1; i < categories.length; i += 1) {
        const prev = toMins(categories[i - 1]);
        const curr = toMins(categories[i]);
        // Skip non-time categories (date/weekday labels) and small gaps.
        if (prev != null && curr != null) {
          // Gap = difference minus one interval (the expected step).
          // Only label gaps larger than 2x the interval to avoid noise.
          const gap = curr - prev - intervalMinutes;
          if (gap > intervalMinutes) {
            const gapHours = Math.floor(gap / 60);
            const gapMinutes = gap % 60;
            const gapLabel = gapHours ? `+${gapHours}h${gapMinutes ? ` ${gapMinutes}min` : ''}` : `+${gapMinutes}min`;
            lines.push({
              value: i - 0.5,
              color: '#ccd3dc',
              width: 1,
              dashStyle: 'Dash',
              label: {
                text: gapLabel,
                style: { fontSize: '10px', color: '#8896ab' },
                verticalAlign: 'middle',
                textAlign: 'center',
              },
            });
          }
        }
      }
      return lines;
    },
    /**
     * Friendly date/period label shown at the top of the tooltip.
     *  - For preset selections (this month, today, etc.) — localized preset name.
     *  - For 'customDate' or single-day ranges — formatted date(s).
     *  - Empty string when the date range isn't yet known.
     */
    periodLabel() {
      const range = this.dateRange;
      if (!range) return '';
      const PRESET_KEYS = {
        today: 'flexigo.date.today',
        yesterday: 'flexigo.date.yesterday',
        thisWeek: 'flexigo.date.thisWeek',
        lastSevenDays: 'flexigo.date.lastSevenDays',
        thisMonth: 'flexigo.date.thisMonth',
        lastThirtyDays: 'flexigo.date.lastThirtyDays',
        lastMonth: 'flexigo.date.lastMonth',
      };
      if (range.type && PRESET_KEYS[range.type]) {
        return this.$t(PRESET_KEYS[range.type]);
      }
      // Custom range or no preset: format start/end dates in the browser locale,
      // matching the axis and tooltip date labels.
      const fmt = (d) => {
        if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
        return format(d, getBrowserLocaleFormat({ type: 'date' }));
      };
      const startStr = fmt(range.startDate);
      const endStr = fmt(range.endDate);
      if (startStr && endStr) {
        return startStr === endStr ? startStr : `${startStr}\u2013${endStr}`;
      }
      return '';
    },
    xAxisTitle() {
      if (this.viewMode === 'byRun') {
        // Single route: exact times — label depends on trip direction (no reference-time toggle here).
        if (this.isSingleRoute) {
          return this.direction === 'INCOMING'
            ? this.$t('flexigo.ridership.arrivalTimeExact')
            : this.$t('flexigo.ridership.departureTimeExact');
        }
        // Multi-route: grouped by interval — label depends on the reference-time toggle.
        const key =
          this.referenceTime === 'lastStopArrival'
            ? 'flexigo.ridership.arrivalTimeGroupedBy'
            : 'flexigo.ridership.departureTimeGroupedBy';
        return this.$t(key, { interval: this.intervalSuffix });
      }
      if (this.viewMode === 'byDay') return this.$t('flexigo.ridership.dayOfWeek');
      return this.$t('flexigo.ridership.date');
    },
    hasOccupancy() {
      return this.occupancy && this.occupancy.length > 0;
    },
    allSeries() {
      const result = this.series.map((s) => ({ ...s, yAxis: 0 }));
      // Vehicle Occupancy series (2nd y-axis) temporarily disabled — bringing it back later.
      // if (this.hasOccupancy) {
      //   result.push({
      //     name: 'Vehicle Occupancy %',
      //     type: 'line',
      //     yAxis: 1,
      //     data: this.occupancy,
      //     color: '#1cab49',
      //     // Break the line on null (no-data) points instead of drawing down to 0.
      //     connectNulls: false,
      //     marker: {
      //       enabled: true,
      //       radius: 4,
      //       symbol: 'circle',
      //       fillColor: 'white',
      //       lineWidth: 2,
      //       lineColor: '#1cab49',
      //     },
      //     tooltip: {
      //       valueSuffix: '%',
      //     },
      //   });
      // }
      return result;
    },
    chartOptions() {
      const isByRun = this.viewMode === 'byRun';

      const yAxes = [
        {
          min: 0,
          title: {
            text: 'Ridership',
            style: { color: '#8896ab', fontSize: '12px' },
            margin: 35,
          },
          gridLineColor: '#f0f0f0',
          stackLabels: { enabled: false },
        },
      ];

      // Vehicle Occupancy 2nd y-axis temporarily disabled — bringing it back later.
      // if (this.hasOccupancy) {
      //   yAxes.push({
      //     min: 0,
      //     max: 100,
      //     title: {
      //       text: 'Vehicle Occupancy %',
      //       style: { color: '#1cab49' },
      //       margin: 35,
      //     },
      //     labels: {
      //       format: '{value}%',
      //       style: { color: '#1cab49' },
      //     },
      //     opposite: true,
      //     gridLineWidth: 0,
      //   });
      // }

      const {
        fromTime,
        toTime,
        viewMode,
        direction,
        isSingleRoute,
        routeName,
        referenceTime,
        intervalMinutes,
        periodLabel,
        fullCategories,
        gapPlotLines,
      } = this;
      const t = this.$t.bind(this);

      // Pre-resolve localized labels so they're available inside the
      // (non-Vue-bound) Highcharts formatter closure.
      const labels = {
        loadFactor: t('flexigo.ridership.loadFactor'),
        vehicleOccupancy: t('flexigo.ridership.vehicleOccupancy'),
        totalRidership: t('flexigo.ridership.totalRidership'),
        dep: t('flexigo.ridership.dep'),
        arr: t('flexigo.ridership.arr'),
        departures: t('flexigo.ridership.departures'),
        arrivals: t('flexigo.ridership.arrivals'),
        numRiders: t('flexigo.ridership.numRiders'),
        route: t('flexigo.ridership.route'),
        total: t('flexigo.ridership.total'),
      };

      // Render a "HH:mm" 24-hour value the way the rest of the app renders a time:
      // `localFormatTimeString` lets the browser locale decide, so an English admin reads
      // "6:00 AM" and a Turkish one "18:00". The chart used to render the analysis doc's
      // one-letter form ("6:00 A") here, which disagreed with every other time on the
      // dashboard, the From/To pickers above it most visibly — they format their own value
      // with this same helper (VSER-14676). The date labels on this chart already follow the
      // same signal, via `getBrowserLocaleFormat`.
      // Anything that isn't a recognizable time (date or weekday labels) is returned
      // untouched, so this is safe to apply to any category label.
      const toClockLabel = (timeStr) => {
        if (!timeStr) return '';
        if (!/^\d{1,2}:\d{2}$/.test(String(timeStr))) return timeStr;
        return localFormatTimeString(timeStr);
      };

      // Localized weekday plural ("Monday" -> "Mondays" in EN, "Pazartesi günleri" in TR).
      const dayPlural = (dayName) => t('flexigo.ridership.dayPlural', { day: dayName });

      const timeRangeStr = fromTime && toTime ? `${toClockLabel(fromTime)}\u2013${toClockLabel(toTime)}` : '';

      // Compute the end of an interval bucket from its start label ("HH:mm" 24h).
      // Returns the end in the locale's clock format, e.g. start="08:00", 30-min ->
      // "8:29 AM" on an English browser and "08:29" on a Turkish one.
      // Returns the input unchanged if startStr isn't a recognizable time.
      const bucketEndStr = (startStr) => {
        if (!startStr || !/^\d{1,2}:\d{2}$/.test(startStr)) return startStr;
        const [h, m] = startStr.split(':').map(Number);
        const endMin = h * 60 + m + intervalMinutes - 1;
        const wrap = (n) => ((n % 1440) + 1440) % 1440;
        const totalEnd = wrap(endMin);
        const eh = Math.floor(totalEnd / 60);
        const em = totalEnd % 60;
        return toClockLabel(`${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`);
      };

      return {
        chart: {
          type: 'column',
          height: 420,
          style: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
        },
        credits: { enabled: false },
        // Native Highcharts export menu is disabled — a custom ExportMenu
        // dropdown (PNG + Excel) lives in the card header, matching the OTP
        // dashboard pattern.
        exporting: { enabled: false },
        title: { text: null },
        xAxis: {
          categories: this.categories,
          lineColor: '#e8ecf1',
          tickColor: '#e8ecf1',
          title: {
            text: this.xAxisTitle,
            style: { color: '#8896ab', fontSize: '12px' },
            margin: 35,
          },
          labels: {
            // Time-of-day axes are always tilted (they're the densest). For the
            // others we offer Highcharts a rotation ladder instead of pinning
            // `rotation: 0` — a fixed rotation switches autoRotation off, so
            // long labels (e.g. weekly buckets over a 4-month range) were being
            // dropped rather than tilted.
            autoRotation: isByRun ? [-45] : [0, -45, -90],
            style: { fontSize: '11px', color: '#666' },
            formatter() {
              // Clock-like categories follow the locale's 12/24-hour
              // convention; date and weekday labels pass through untouched.
              return toClockLabel(this.value);
            },
          },
          gridLineWidth: 0,
          // Dashed plotLines with "+3h" labels at gaps in time-of-day view
          // (e.g. between 10:00 and 14:00 when no trips ran in between).
          plotLines: gapPlotLines,
        },
        yAxis: yAxes,
        legend: { enabled: false },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#2d3548',
          borderColor: '#2d3548',
          borderRadius: 10,
          shadow: { color: 'rgba(0,0,0,0.2)', offsetX: 0, offsetY: 4, opacity: 1, width: 12 },
          style: { color: '#fff', fontSize: '13px' },
          formatter() {
            // The axis shows the compact label; the tooltip has room for the
            // long one (full date incl. year) when the parent supplies it.
            const pointIndex = this.points?.[0]?.point?.index;
            const categoryLabel = (pointIndex != null && fullCategories[pointIndex]) || this.x;

            if (isSingleRoute) {
              // Single-route tooltip — header & metric label depend on viewMode (per spec):
              //   byRun  (Time of Day): '<Period>, <time> <Dep./Arr.>'    + Vehicle Occupancy
              //   byDate (Date)       : '<Date>, All <ref> between <fromTime>–<toTime>' + Load Factor
              //   byDay  (Weekday)    : '<Period>, <Day pluralized>'      + Vehicle Occupancy
              // Direction word for single-route comes from the route's own direction
              // (passed in via `direction` prop, which the parent resolves to the route's
              // direction for single-route — there is no reference-time toggle here).
              const isInbound = direction === 'INCOMING';
              const refShort = isInbound ? labels.arr : labels.dep;
              const refLong = isInbound ? labels.arrivals : labels.departures;

              // Authoritative per-bar breakdown injected into each data point by the
              // dashboard module's singleRoute*Data computeds.
              const firstPoint = this.points && this.points[0];
              const bucketData = firstPoint?.point?.options?.bucketData ||
                firstPoint?.point?.bucketData || { stops: [], totalOn: 0, loadFactor: null };
              const { stops = [], totalOn = 0 } = bucketData;

              let header;
              if (viewMode === 'byRun') {
                const left = periodLabel ? `${periodLabel}, ` : '';
                header = `${left}${toClockLabel(categoryLabel)} ${refShort}`;
              } else if (viewMode === 'byDay') {
                const left = periodLabel ? `${periodLabel}, ` : '';
                header = `${left}${dayPlural(categoryLabel)}`;
              } else {
                // byDate
                const between = timeRangeStr ? ` between ${timeRangeStr}` : '';
                header = `${categoryLabel}, All ${refLong}${between}`;
              }

              // The header carries the route's own name, from the same admin-entered list the
              // rows below draw theirs from, so it is escaped the same way.
              let html = `<div style="font-weight:600;margin-bottom:2px">${escapeHtml(routeName)}</div>`;
              html += `<div style="color:#cbd5e0;font-size:12px;margin-bottom:4px">${header}</div>`;
              html += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';

              // Load Factor / Vehicle Occupancy row temporarily disabled \u2014 bringing it back later.
              // metricLabel was `labels.vehicleOccupancy` for byRun/byDay, `labels.loadFactor` for byDate.
              // const metricText = loadFactor != null ? `${loadFactor}%` : '\u2014';
              // html += `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#cbd5e0">${metricLabel}</span><span style="font-weight:600;text-align:right;min-width:48px">${metricText}</span></div>`;
              html += `<div style="display:flex;justify-content:space-between;gap:16px;margin-bottom:6px"><span style="color:#cbd5e0">${labels.totalRidership}</span><span style="font-weight:600;text-align:right;min-width:48px">${totalOn}</span></div>`;

              stops.forEach(({ name, value }) => {
                html += `<div style="display:flex;justify-content:space-between;gap:16px">${tooltipNameCell(
                  name,
                )}<span style="font-weight:600;text-align:right;min-width:48px">${value}</span></div>`;
              });
              return `<div style="max-height:300px;overflow-y:auto;${TOOLTIP_SCROLL_STYLE}">${html}</div>`;
            }

            // Multi-route tooltip header — format depends on the active view mode (per spec):
            //  - byRun  (Time of Day): '<Period>, All trips <ref> between <bucketStart>–<bucketEnd>'
            //  - byDate (Date)       : '<Date>, All trips <ref> between <fromTime>–<toTime>'
            //  - byDay  (Weekday)    : '<Period>, <Day>s'  (line 85)
            // The ref word ('arriving'|'departing') is driven by the reference-time toggle
            // (per spec lines 58–59), NOT the direction filter.
            const refWord = referenceTime === 'lastStopArrival' ? 'arriving' : 'departing';
            let headerText;
            if (viewMode === 'byRun') {
              // bucketEndStr already applies the locale's clock format; do the same to the start.
              const bucketRange = `${toClockLabel(categoryLabel)}\u2013${bucketEndStr(categoryLabel)}`;
              const left = periodLabel ? `${periodLabel}, ` : '';
              headerText = `${left}All trips ${refWord} between ${bucketRange}`;
            } else if (viewMode === 'byDay') {
              const left = periodLabel ? `${periodLabel}, ` : '';
              headerText = `${left}${dayPlural(categoryLabel)}`;
            } else {
              // byDate — categoryLabel is the bar's date, time range is the global From/To
              // (already in the locale's clock format in `timeRangeStr`).
              const between = timeRangeStr ? ` between ${timeRangeStr}` : '';
              headerText = `${categoryLabel}, All trips ${refWord}${between}`;
            }
            // Sticky header (date/context + column labels) stays pinned above
            // the scrollable route list so the user always knows what they're
            // looking at, even when scrolling through 60+ routes.
            let header = `<div style="font-weight:600;margin-bottom:2px">${headerText}</div>`;
            header += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';
            // Vehicle Occupancy column temporarily disabled \u2014 bringing it back later.
            // header += `<div style="display:flex;justify-content:space-between;gap:8px;color:#cbd5e0"><span>${labels.route}</span><span style="display:flex;gap:16px"><span style="width:64px;text-align:right;flex-shrink:0">${labels.numRiders}</span><span style="width:120px;text-align:right;flex-shrink:0">${labels.vehicleOccupancy}</span></span></div>`;
            header += `<div style="display:flex;justify-content:space-between;gap:8px;color:#cbd5e0"><span>${labels.route}</span><span style="width:64px;text-align:right;flex-shrink:0">${labels.numRiders}</span></div>`;
            header += '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>';

            let total = 0;
            let routeRows = '';

            this.points.forEach((point) => {
              if (point.series.yAxis.options.index === 1) return;
              if (point.y > 0) {
                // Vehicle Occupancy column temporarily disabled \u2014 bringing it back later.
                // const rawOpts = point.point.options;
                // const { occupancy } = rawOpts && typeof rawOpts === 'object' ? rawOpts : {};
                // const occText = occupancy != null ? `${occupancy}%` : '\u2014';
                // routeRows += `<div style="display:flex;justify-content:space-between;gap:8px">${tooltipNameCell(
                //   point.series.name,
                // )}<span style="display:flex;gap:16px"><span style="width:64px;text-align:right;flex-shrink:0;font-weight:600">${
                //   point.y
                // }</span><span style="width:120px;text-align:right;flex-shrink:0;font-weight:600">${occText}</span></span></div>`;
                routeRows += `<div style="display:flex;justify-content:space-between;gap:8px">${tooltipNameCell(
                  point.series.name,
                )}<span style="width:64px;text-align:right;flex-shrink:0;font-weight:600">${point.y}</span></div>`;
                total += point.y;
              }
            });

            // Sticky footer (total row) stays pinned below the scroll area.
            // Vehicle Occupancy total temporarily disabled \u2014 bringing it back later.
            // const totalOcc = totalOccCount > 0 ? `${Math.round(totalOccLoad / totalOccCount)}%` : '\u2014';
            const footer =
              '<div style="border-top:1px solid #4a5568;margin:6px 0"></div>' +
              `<div style="display:flex;justify-content:space-between;gap:8px"><span style="color:#fff;font-weight:700">${labels.total}</span><span style="width:64px;text-align:right;flex-shrink:0;font-weight:700;color:#fff">${total}</span></div>`;

            return `${header}<div style="max-height:240px;overflow-y:auto;${TOOLTIP_SCROLL_STYLE}">${routeRows}</div>${footer}`;
          },
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            borderWidth: 0,
            borderRadius: 0,
            pointPadding: 0.15,
            groupPadding: 0.1,
            dataLabels: { enabled: false },
          },
        },
        series: this.allSeries,
      };
    },
  },
};
</script>

<style scoped>
.ridership-by-route-chart {
  width: 100%;
  min-height: 420px;
}
</style>
